import { Auth0ContextInterface, User } from '@auth0/auth0-react'
import { Configuration, BaseAPI, Middleware } from '@Generated/back-office-client/src'
import { getAuthConfig } from '../../config'
import { store } from '@AppState/redux-store'
import { AppVersionActions } from '@AppState/app-version/app-version-slice'

type APIConfig = {
  basePath: string
  customerId: string
}

const cachedClients: { [key: string]: object } = {}

let BACK_OFFICE_DATA_API_CONFIG: APIConfig
let AUTH_0_CONTEXT: Auth0ContextInterface<User>

const redirectToLogin = () => {
  const organization = getAuthConfig().authorizationParams?.organization
  AUTH_0_CONTEXT.loginWithRedirect({
    authorizationParams: { prompt: 'none', organization },
    appState: { returnTo: window.location.pathname },
  })
}

/**
 * Wraps Auth0's getAccessTokenSilently with a guard against falsy tokens.
 * If the token is empty/undefined, redirects to login instead of sending an unauthenticated request.
 */
const getAccessToken = async (): Promise<string> => {
  const token = await AUTH_0_CONTEXT.getAccessTokenSilently()
  if (!token) {
    redirectToLogin()
    throw new Error('No access token available')
  }
  return token
}

const configureApis = (config: APIConfig, auth0Context: Auth0ContextInterface<User>) => {
  BACK_OFFICE_DATA_API_CONFIG = config
  AUTH_0_CONTEXT = auth0Context
}

const buildVersionMiddleware: Middleware = {
  post: async (context) => {
    const version = context.response.headers.get('x-build-version')
    if (version) {
      store.dispatch(AppVersionActions.recordVersion(version))
    }
    return context.response
  },
}

const getBackOfficeApi = <T extends BaseAPI>(apiClass: new (config: Configuration) => T) => {
  const existingInstance = cachedClients[apiClass.name]

  if (existingInstance) {
    return existingInstance as T
  }

  const configuration = new Configuration({
    ...BACK_OFFICE_DATA_API_CONFIG,
    accessToken: getAccessToken,
    middleware: [buildVersionMiddleware],
  })

  const instance = new apiClass(configuration)
  cachedClients[apiClass.name] = instance

  return instance
}

const getBackOfficeConfig = () => {
  return BACK_OFFICE_DATA_API_CONFIG
}

const setCustomerForRequest = (customerId: string) => {
  if (BACK_OFFICE_DATA_API_CONFIG) {
    BACK_OFFICE_DATA_API_CONFIG.customerId = customerId
  }
}

export const BackOfficeDataServiceAPI = {
  configureApis,
  getBackOfficeConfig,
  setCustomerForRequest,
  getBackOfficeApi,
  getAccessToken,
}
